import GetSetupCard from "@/components/GetSetupCard";
import { useNearbyJob } from "@/lib/nearbyJob";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { fmtDate, fmtDay, fmtTime, fmtWhen, fmtMoney, wxCondition } from "@/lib/format";
import PhotoImg from "@/components/PhotoImg";
import { TrialReminder } from "@/components/OnboardingChecklist";
import { useAuth } from "@/contexts/AuthContext";
import { HardHat, Camera, ClipboardList, Plus, ArrowRight, Calendar, CalendarClock, Mic, Play, Square, AlertTriangle, FileText, Receipt, Navigation, Loader2, CloudRain, BarChart3, Truck } from "lucide-react";
import { toast } from "sonner";
import CatchUpSheet from "@/components/CatchUpSheet";
import CameraCapture from "@/components/CameraCapture";
import TomorrowReady from "@/components/TomorrowReady";
import RainDaySheet from "@/components/RainDaySheet";
import ShareLinkSheet from "@/components/ShareLinkSheet";

// Home — "one job, one glance, one tap": Continue Work, Today, Recent
// Progress, Needs Attention, then quick actions. Clock-in stays in the
// header; 360 Update stays available but never dominates project/photo content.
export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [unread, setUnread] = useState(0);
  const [ob, setOb] = useState(null);
  const [camJob, setCamJob] = useState(null); // today's scheduled job → camera
  const [nearProjects, setNearProjects] = useState([]);
  useEffect(() => {
    api.get("/projects").then(({ data }) => setNearProjects((Array.isArray(data) ? data : data.items || [])
      .filter((p) => !p.deleted_at && p.status !== "archived"))).catch(() => {});
  }, []);
  const nearby = useNearbyJob(nearProjects, (user?.notif_prefs?.nearby_first) !== false);
  // Batch 6: assistant layer — morning brief (rain nudges + needs-you), EOD recap, weekly report
  const [brief, setBrief] = useState(null);
  const [recap, setRecap] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [rainDate, setRainDate] = useState("");
  const [rainOpen, setRainOpen] = useState(false);
  const isManager = ["owner", "admin"].includes(user?.role);
  const loadBrief = () => api.get("/assistant360/morning-brief").then(({ data }) => setBrief(data)).catch(() => {});
  // Sub insurance alerts on the home Needs-Attention card (owner/admin/office).
  const [subAlerts, setSubAlerts] = useState([]);
  const [certShare, setCertShare] = useState(null); // {url, phone}
  useEffect(() => {
    if (["owner", "admin", "office"].includes(user?.role))
      api.get("/schedule/my-day").then(({ data }) => setSubAlerts(data.sub_alerts || [])).catch(() => {});
  }, [user?.role]);
  const askCert = async (a) => {
    try {
      const { data } = await api.post(`/subs/${a.sub_id}/portal-link`, {});
      setCertShare({ url: `${window.location.origin}${data.path}`, phone: a.phone || "" });
    } catch { toast.error(t("toast_failed")); }
  };
  // Truck maintenance reminders (oil change by meter / registration by date)
  // ride the home Needs-Attention card too (owner/admin/office).
  const [truckReminders, setTruckReminders] = useState([]);
  useEffect(() => {
    if (["owner", "admin", "office"].includes(user?.role))
      api.get("/dashboard").then(({ data }) => setTruckReminders(data.maintenance_reminders || [])).catch(() => {});
  }, [user?.role]);
  const nyRows = (brief?.needs_you || []).filter((r) => r.type !== "overdue"); // dashboard already covers overdue tasks
  const [drawRow, setDrawRow] = useState(null); // draw_invoice needs-you row → preview sheet
  const [catchupOpen, setCatchupOpen] = useState(false); // past-due schedule items → Catch-up sheet
  const overdueSched = data?.overdue_schedule || [];
  const [drawBusy, setDrawBusy] = useState(false);
  const [drawShare, setDrawShare] = useState(null); // post-create: send-to-customer share sheet
  const createDrawInvoice = async () => {
    if (!drawRow || drawBusy) return;
    setDrawBusy(true);
    try {
      const { data: res } = await api.post(`/moneyv2/schedule/${drawRow.id}/draw-invoice`);
      toast.success(t("draw_created", { number: res.number }));
      const label = drawRow.draw_label;
      setDrawRow(null);
      loadBrief();
      try {
        const { data: sh } = await api.post(`/moneyv2/invoices/${res.id}/share-link`);
        setDrawShare({ url: sh.url, label, phone: sh.customer_phone || "", email: sh.customer_email || "", invId: res.id });
      } catch { /* share link optional — the invoice is waiting in Money */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("toast_failed"));
    } finally { setDrawBusy(false); }
  };

  const loadOb = () => api.get("/onboarding/status").then(({ data }) => setOb(data)).catch(() => {});

  const loadDash = () => api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => {
    loadDash();
    api.get("/notifications/unread-count?kind=estimate_request").then(({ data }) => setUnread(data.count)).catch(() => {});
    loadOb();
    loadBrief();
    if (isManager) api.get("/assistant360/weekly-report").then(({ data }) => setWeekly(data)).catch(() => {});
    if (new Date().getHours() >= 15) api.get("/assistant360/eod-recap").then(({ data }) => setRecap(data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute "Today" whenever the app returns to the foreground — the local
  // date may have rolled over while the app sat in the background.
  useEffect(() => {
    const onVis = () => { if (!document.hidden) loadDash(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissReminder = (key) => {
    api.post("/onboarding/reminder/dismiss", { key }).catch(() => {});
    setOb((p) => (p ? { ...p, reminder: null } : p));
  };

  const dismissUnread = () => {
    api.post("/notifications/read-all").catch(() => {});
    setUnread(0);
  };

  const todayLong = new Date().toLocaleDateString(i18n.language?.startsWith("es") ? "es-ES" : "en-US",
    { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const fin = data?.fin;
  const pendingCount = (data?.overdue_tasks?.length || 0) + (fin?.estimates_awaiting || 0) +
    (fin?.invoices_overdue || 0) + (data?.missing_evening || 0) + (data?.walkthroughs_ready?.length || 0);

  // Needs-attention card: red items (expired insurance, overdue tasks/catch-up,
  // overdue invoices, overdue truck service) pin it right under the hero row;
  // otherwise it stays in its usual spot further down.
  const attentionHasRed = subAlerts.some((a) => a.status === "expired") || (data?.overdue_tasks || []).length > 0
    || overdueSched.length > 0 || (fin?.invoices_overdue || 0) > 0 || truckReminders.some((r) => r.overdue);
  const showAttention = !!data && (pendingCount > 0 || nyRows.length > 0 || subAlerts.length > 0 || truckReminders.length > 0);

  const attentionCard = () => (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="home-attention">
      <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />{t("home_needs_attention")}
      </h2>
      <div className="mt-3 space-y-2">
        {subAlerts.map((a) => (
          <div key={`sub-${a.sub_id}-${a.kind}`} data-testid={`home-sub-alert-${a.sub_id}-${a.kind}`}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 min-h-[48px] ${a.status === "expired" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
            <AlertTriangle className={`h-4 w-4 shrink-0 ${a.status === "expired" ? "text-red-600" : "text-amber-600"}`} />
            <span className={`min-w-0 flex-1 text-sm font-semibold ${a.status === "expired" ? "text-red-700" : "text-amber-800"}`}>
              {a.name} · {a.kind === "liability" ? t("subs_liab") : t("subs_comp")}{" "}
              {a.status === "expired" ? t("subs_ins_expired") : t("subs_ins_days", { days: a.days })}
            </span>
            <button onClick={() => askCert(a)} data-testid={`home-ask-cert-${a.sub_id}-${a.kind}`}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-bold active:scale-95 ${a.status === "expired" ? "border-red-300 text-red-700" : "border-amber-300 text-amber-800"}`}>
              {t("subs_ask_cert")}
            </button>
          </div>
        ))}
        {truckReminders.map((r, i) => (
          <Link key={`tr-${i}`} to="/app/fleet" data-testid={`home-truck-reminder-${i}`}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 min-h-[48px] ${r.overdue ? "border-red-100 bg-red-50" : "border-amber-100 bg-amber-50"}`}>
            <Truck className={`h-4 w-4 shrink-0 ${r.overdue ? "text-red-600" : "text-amber-600"}`} />
            <span className={`min-w-0 flex-1 truncate text-sm font-semibold ${r.overdue ? "text-red-700" : "text-amber-800"}`}>{r.text}</span>
          </Link>
        ))}
        {(data.overdue_tasks || []).length > 0 && (
          <Link to="/app/projects?f=attention" data-testid="home-overdue-tasks"
            className="flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 min-h-[48px]">
            <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />
            <span className="flex-1 text-sm font-semibold text-red-700">{t("home_overdue_tasks")}</span>
            <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-black text-white" aria-label={t("home_overdue_tasks")}>{data.overdue_tasks.length}</span>
          </Link>
        )}
        {data.open_trip && (
          <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 min-h-[48px]" data-testid="home-open-trip">
            <Truck className="h-4 w-4 shrink-0 text-amber-700" />
            <span className="flex-1 text-sm font-semibold text-amber-800">{t("myday_trip_in_progress")} · {data.open_trip.vehicle_name}</span>
            <button onClick={() => navigate(`/app/financials?tab=expenses`)} data-testid="home-finish-trip"
              className="shrink-0 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-bold text-white">{t("ad_finish", "Finish")}</button>
          </div>
        )}
        {overdueSched.length > 0 && (
          <button onClick={() => setCatchupOpen(true)} data-testid="home-catchup-open"
            className="flex w-full items-center gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 min-h-[48px] text-left active:scale-[0.99]">
            <CalendarClock className="h-4 w-4 shrink-0 text-red-600" />
            <span className="flex-1 text-sm font-semibold text-red-700">{t("catchup_home", { count: overdueSched.length })}</span>
            <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-black text-white">{overdueSched.length}</span>
          </button>
        )}
        {nyRows.map((r, i) => r.type === "draw_invoice" ? (
          <button key={`ny-${i}`} type="button" onClick={() => setDrawRow(r)} data-testid={`home-needs-you-draw-${i}`}
            className="flex w-full items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 min-h-[48px] text-left transition-colors hover:bg-green-100">
            <Receipt className="h-4 w-4 shrink-0 text-green-600" />
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-green-800">
              {t("ny_draw_invoice", { label: r.draw_label, amount: fmtMoney(r.amount_cents) })}{r.project_name ? ` · ${r.project_name}` : ""}
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-green-500" />
          </button>
        ) : (
          <Link key={`ny-${i}`} to="/app/schedule" data-testid={`home-needs-you-${i}`}
            className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 min-h-[48px]">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-amber-800">
              {t(`ny_${r.type}`, { name: r.sub_name || "" })}: {r.title}{r.project_name ? ` · ${r.project_name}` : ""}
            </span>
          </Link>
        ))}
        {fin?.estimates_awaiting > 0 && (
          <Link to="/app/financials" data-testid="home-est-awaiting"
            className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 min-h-[48px]">
            <FileText className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="flex-1 text-sm font-semibold text-amber-800">{t("home_est_awaiting")}</span>
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-black text-white">{fin.estimates_awaiting}</span>
          </Link>
        )}
        {fin?.invoices_overdue > 0 && (
          <Link to="/app/financials" data-testid="home-inv-overdue"
            className="flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 min-h-[48px]">
            <Receipt className="h-4 w-4 shrink-0 text-red-600" />
            <span className="flex-1 text-sm font-semibold text-red-700">{t("home_inv_overdue")}</span>
            <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-black text-white">{fin.invoices_overdue}</span>
          </Link>
        )}
        {data.missing_evening > 0 && (
          <Link to="/app/time" data-testid="home-missing-evening"
            className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 min-h-[48px]">
            <ClipboardList className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="flex-1 text-sm font-semibold text-amber-800">{t("home_missing_evening")}</span>
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-black text-white">{data.missing_evening}</span>
          </Link>
        )}
        {(data.walkthroughs_ready || []).map((w) => (
          <Link key={w.id} to={`/app/projects/${w.project_id}?tab=activity&walkthrough=${w.id}`} data-testid={`home-wt-ready-${w.id}`}
            className="flex items-center gap-3 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 min-h-[48px]">
            <Play className="h-4 w-4 shrink-0 text-sky-600" />
            <span className="flex-1 text-sm font-semibold text-sky-800">{t("wt_ready")}{w.project_name ? ` · ${w.project_name}` : ""}</span>
          </Link>
        ))}
      </div>
    </section>
  );

  return (
    <div className="space-y-6" data-testid="page-home">
      {/* Header: greeting, date, clock status */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500 font-semibold" data-testid="home-date">{todayLong}</div>
          <h1 className="mt-1 font-heading text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            {t("home_hello", { name: user?.name || "" })}
          </h1>
          <div className="mt-1 text-sm font-semibold" data-testid="home-clock-status">
            {data?.clocked_in
              ? <span className="text-green-700">{t("home_clocked_in_at", { project: data.clock_project?.name || "" })}</span>
              : <span className="text-slate-500">{t("home_not_clocked")}</span>}
          </div>
        </div>
      </div>

      {/* Top actions — immediately under the greeting: photo, project, schedule */}
      {/* Self-serve setup for new companies — pinned to the top of Home (Group D) */}
      <GetSetupCard />

      <div className="space-y-2" data-testid="home-actions">
        {/* Pre-launch cleanup (James B): one BIG camera action — 360 card and
            duplicate-project removed from Home (header 360 button stays;
            duplicate moved to the job ⋯ menu on Projects). */}
        <QuickAction onClick={() => window.dispatchEvent(new Event("bf:open-camera"))} icon={Camera} label={t("action_take_photo")} primary big testid="home-take-photos" />
        {(data?.projects_count ?? 0) > 0 && (
        <div className="grid grid-cols-2 gap-2">
          <QuickAction to="/app/projects/new" icon={Plus} label={t("action_new_project")} testid="home-new-project" />
          <QuickAction to="/app/schedule?add=1" icon={Calendar} label={t("home_add_schedule")} testid="home-add-schedule" />
        </div>
        )}
      </div>

      <TrialReminder status={ob} onDismiss={dismissReminder} />

      {/* Red-flag items pin the Needs-attention card right under the hero row */}
      {showAttention && attentionHasRed && attentionCard()}

      {!data && (
        <div className="space-y-3" data-testid="home-skeleton">
          <div className="bf-skeleton h-24" />
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => <div key={i} className="bf-skeleton h-24" />)}
          </div>
        </div>
      )}

      {/* "You're here" — the job you're standing at, first, with fast actions */}
      {nearby && (
        <section className="rounded-2xl border border-green-200 bg-green-50 p-4" data-testid="home-nearby">
          <div className="text-sm font-extrabold text-slate-900" data-testid="home-nearby-title">
            {t("home_youre_at", { name: nearby.project.name })}
          </div>
          <div className="mt-2 flex gap-2">
            <button onClick={() => setCamJob({ id: nearby.project.id, name: nearby.project.name })} data-testid="home-nearby-camera"
              className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-slate-900 px-4 text-sm font-bold text-white active:scale-95">
              <Camera className="h-4 w-4" />{t("action_take_photo")}
            </button>
            <button onClick={() => window.dispatchEvent(new Event("bf:open-quick-add"))} data-testid="home-nearby-add"
              className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 active:scale-95">
              <Plus className="h-4 w-4" />{t("qa_title", "Quick add")}
            </button>
          </div>
        </section>
      )}

      {/* Today — schedule cards */}
      {data && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="home-today">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("home_today_title")}</h2>
          <div className="mt-3 space-y-2">
            {(data.today_schedule || []).map((s) => (
              <div key={s.id} className="rounded-xl border border-slate-100 bg-slate-50" data-testid={`home-job-${s.id}`}>
                <Link to={s.project_id ? `/app/projects/${s.project_id}` : "/app/schedule"}
                  className="flex items-center gap-3 px-4 py-3 min-h-[48px] active:scale-[0.99]">
                  <Calendar className="h-4 w-4 shrink-0 text-brand" />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                    {s.title || s.project_name || t("nav_schedule")}
                    {s.project_name ? <span className="block truncate text-[11px] font-medium text-slate-500">{s.project_name}</span> : null}
                  </span>
                  <span className="text-xs font-bold text-slate-500">{(s.date || "").length > 10 ? fmtTime(s.date, { hour: "numeric", minute: "2-digit" }) : t("sched_all_day")}</span>
                </Link>
                {s.project_id ? (
                  <div className="flex items-center gap-1 border-t border-slate-100 px-3 py-1.5">
                    <button onClick={() => setCamJob({ id: s.project_id, name: s.project_name })} data-testid={`home-job-camera-${s.id}`} aria-label={t("action_take_photo")}
                      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-brand active:scale-95"><Camera className="h-4 w-4" /></button>
                    {s.address ? (
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}`} target="_blank" rel="noreferrer"
                        data-testid={`home-job-nav-${s.id}`} aria-label={t("action_navigate")}
                        className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-brand active:scale-95"><Navigation className="h-4 w-4" /></a>
                    ) : null}
                    <button onClick={() => window.dispatchEvent(new CustomEvent("bf:open-assistant", { detail: { mode: "update", projectId: s.project_id, projectName: s.project_name || "" } }))} data-testid={`home-job-360-${s.id}`} aria-label="360 Assistant"
                      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-brand active:scale-95"><Mic className="h-4 w-4" /></button>
                  </div>
                ) : null}
              </div>
            ))}
            {(data.today_schedule || []).length === 0 && (
              <p className="py-1 text-sm text-slate-500" data-testid="home-all-clear">{t("home_nothing_today")}</p>
            )}
          </div>
        </section>
      )}

      {/* Batch 6: rain-shift nudges from the morning brief */}
      {(brief?.rain_nudges || []).length > 0 && (
        <section className="rounded-2xl border border-sky-200 bg-sky-50 p-5" data-testid="home-rain-nudges">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sky-700">
            <CloudRain className="h-3.5 w-3.5" />{t("mb_rain_title")}
          </h2>
          <div className="mt-3 space-y-2">
            {brief.rain_nudges.map((n, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-sky-200 bg-white px-4 py-3" data-testid={`rain-nudge-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-slate-900">{n.project_name}</div>
                  <div className="text-xs text-slate-500">
                    {fmtDay(n.date)}{n.prob != null ? ` · ${n.prob}%` : ""} · {t("mb_outdoor_n", { n: n.tasks })}
                  </div>
                </div>
                <button onClick={() => { setRainDate(n.date); setRainOpen(true); }} data-testid={`rain-nudge-shift-${i}`}
                  className="min-h-[44px] shrink-0 rounded-full bg-brand px-4 text-xs font-bold text-white active:scale-95">
                  {t("mb_rain_cta")}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Continue Work — project card with cover + next scheduled item */}
      {data?.recent_project && (
        <Link to={`/app/projects/${data.recent_project.id}`} data-testid="home-continue-project"
          className="bf-card bf-card-hover flex items-center gap-4 p-4">
          {data.recent_project.cover_photo_id ? (
            <PhotoImg photoId={data.recent_project.cover_photo_id} thumb alt={data.recent_project.name}
              className="h-16 w-16 shrink-0 rounded-xl border border-slate-200 object-cover" testid="home-continue-cover" />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-brand/15 text-branddeep">
              <HardHat className="h-7 w-7" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("home_continue")}</div>
            <div className="font-heading font-bold text-slate-900 truncate">{data.recent_project.name}</div>
            {data.recent_project.address && <div className="text-xs text-slate-500 truncate">{data.recent_project.address}</div>}
            {data.next_activity && (
              <div className="mt-1 text-xs font-semibold text-brand" data-testid="home-next-activity">
                {t("home_next_up")}: {data.next_activity.title || t("nav_schedule")} · {fmtDay(String(data.next_activity.date || "").slice(0, 10))}
              </div>
            )}
          </div>
          <ArrowRight className="h-5 w-5 shrink-0 text-brand" />
        </Link>
      )}

      {/* Coming up — tomorrow's work, never mixed into Today */}
      {data && (data.tomorrow_schedule || []).length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="home-coming-up">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("home_coming_up")}</h2>
          <div className="mt-3 space-y-2">
            {data.tomorrow_schedule.map((s) => (
              <Link key={s.id} to="/app/schedule" data-testid={`home-upcoming-${s.id}`}
                className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 min-h-[48px] active:scale-[0.99]">
                <Calendar className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{s.title || t("nav_schedule")}</span>
                <span className="text-xs font-bold text-slate-500">{(s.date || "").length > 10 ? `${t("sched_quick_tomorrow")}, ${fmtTime(s.date, { hour: "numeric", minute: "2-digit" })}` : t("sched_all_day")}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Batch 6: end-of-day recap (evenings) + weekly owner report */}
      {recap && (recap.done_count > 0 || recap.photos_count > 0 || recap.slipped.length > 0) && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="home-recap">
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("eod_title")}</h2>
          <p className="mt-2 text-sm font-semibold text-slate-800" data-testid="recap-line">
            {t("eod_line", { done: recap.done_count, photos: recap.photos_count, clock: recap.clock_ins })}
          </p>
          {recap.slipped.length > 0 && (
            <p className="mt-1 text-xs font-semibold text-amber-700" data-testid="recap-slipped">{t("eod_slipped", { n: recap.slipped.length })}</p>
          )}
          {recap.tomorrow_first?.[0] && (
            <p className="mt-1 text-xs text-slate-500" data-testid="recap-tomorrow">{t("eod_tomorrow", { title: recap.tomorrow_first[0].title })}</p>
          )}
        </section>
      )}
      {isManager && weekly && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="home-weekly">
          <button onClick={() => setWeeklyOpen(true)} data-testid="home-weekly-open"
            className="flex w-full items-center gap-3 text-left active:scale-[0.99]">
            <BarChart3 className="h-4 w-4 shrink-0 text-brand" />
            <span className="flex-1 text-sm font-bold text-slate-900">{t("wr_btn")}</span>
            <span className="text-xs font-semibold text-slate-500">
              {t("wr_summary", { done: weekly.tasks_done, delays: weekly.delays_total })}
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
        </section>
      )}

      {/* Needs Attention — red items are pinned under the hero row above; the
          rest render here (see attentionCard). */}
      {showAttention && !attentionHasRed && attentionCard()}

      {/* Day tools — Start/Finish My Day (after Needs attention, per pre-launch order) */}
      <section className="flex flex-wrap gap-2" data-testid="home-day-tools">
        <button onClick={() => navigate("/app/time")} data-testid="home-day-toggle"
          className={`inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full px-6 text-sm font-bold text-white shadow-lg active:scale-95 ${data?.clocked_in ? "bg-slateink" : "bg-brand"}`}>
          {data?.clocked_in ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {data?.clocked_in ? t("home_finish_day") : t("home_start_day")}
        </button>
      </section>

      {/* Recent Progress — photo strip */}
      {data?.recent_photos?.length > 0 && (
        <section data-testid="home-recent-progress">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-slate-900 text-lg">{t("home_recent_progress")}</h2>
            <Link to="/app/photos" className="text-xs font-semibold text-brand hover:underline flex items-center gap-1" data-testid="link-see-all-photos">
              {t("see_all")} <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {data.recent_photos.map((p) => (
              <PhotoImg key={p.id} photoId={p.id} mediaType={p.media_type} thumb alt={p.note || "photo"}
                className="aspect-square w-full object-cover rounded-xl border border-slate-200 transition-transform duration-200 hover:scale-[1.04]"
                testid={`home-photo-${p.id}`} />
            ))}
          </div>
        </section>
      )}

      {certShare && (
        <ShareLinkSheet open onClose={() => setCertShare(null)} title={t("subs_portal_title")}
          message={t("subs_cert_msg")} url={certShare.url} phone={certShare.phone} />
      )}
      <CameraCapture projectId={camJob?.id} projectName={camJob?.name || ""} open={!!camJob} onClose={() => setCamJob(null)} onUploaded={() => {}}
        onFinish={(pid) => navigate(pid ? `/app/photos?project=${pid}` : "/app/photos")} />

      {catchupOpen && (
        <CatchUpSheet
          items={overdueSched}
          user={user}
          onClose={() => setCatchupOpen(false)}
          onChanged={loadDash}
        />
      )}

      <RainDaySheet open={rainOpen} onClose={() => setRainOpen(false)} initialDate={rainDate}
        onApplied={(d) => {
          toast.success(t("rain_done", { n: d.moved, date: d.shifted_to }), d.undo_id ? {
            action: { label: t("gantt_undo"), onClick: async () => { try { await api.post(`/schedule/move/${d.undo_id}/undo`); loadBrief(); loadDash(); } catch { toast.error(t("toast_failed")); } } },
            duration: 9000,
          } : {});
          loadBrief(); loadDash();
        }} />

      {weeklyOpen && weekly && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setWeeklyOpen(false)} data-testid="weekly-modal">
          <div className="max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-5 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-base font-extrabold text-slate-900">{t("wr_title")}</h3>
            <p className="mt-0.5 text-xs text-slate-500">{fmtDay(weekly.week_start)} → {fmtDay(weekly.week_end)}</p>
            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="weekly-stats">
              {[
                [t("wr_done"), weekly.tasks_done, "wr-stat-done"],
                [t("wr_moves"), weekly.schedule_moves, "wr-stat-moves"],
                [t("wr_photos"), weekly.photos, "wr-stat-photos"],
                [t("wr_delays"), weekly.delays_total, "wr-stat-delays"],
                [t("wr_upcoming"), weekly.upcoming_next_week, "wr-stat-upcoming"],
              ].map(([label, val, tid2]) => (
                <div key={tid2} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center" data-testid={tid2}>
                  <div className="font-heading text-xl font-extrabold text-slate-900">{val}</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
                </div>
              ))}
            </div>
            {weekly.delays_by_cause.length > 0 && (
              <div className="mt-4" data-testid="weekly-causes">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("wr_causes")}</div>
                <div className="mt-2 space-y-1.5">
                  {weekly.delays_by_cause.map((c) => (
                    <div key={c.caused_by} className="flex items-center gap-2 rounded-xl border border-slate-100 px-3 py-2" data-testid={`wr-cause-${c.caused_by}`}>
                      <span className="flex-1 text-sm font-semibold text-slate-800">{t(`delay_cause_${c.caused_by}`, c.caused_by)}</span>
                      <span className="text-xs font-bold text-slate-500">{c.count}× · {t("wr_days_lost", { n: c.days })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button onClick={async () => {
              try { const { data: blob } = await api.get("/reports/weekly.pdf", { responseType: "blob" });
                const url = URL.createObjectURL(blob); const a = document.createElement("a");
                a.href = url; a.download = `weekly-report-${weekly.week_start}.pdf`; a.click(); URL.revokeObjectURL(url);
              } catch { /* retryable */ }
            }} data-testid="weekly-download-pdf"
              className="mt-4 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-brand text-sm font-bold text-white active:scale-95">
              {t("wr_download_pdf", "Download PDF")}
            </button>
            <button onClick={() => setWeeklyOpen(false)} data-testid="weekly-close"
              className="mt-4 flex min-h-[44px] w-full items-center justify-center rounded-full border border-slate-300 text-sm font-bold text-slate-600 active:scale-95">
              {t("action_done")}
            </button>
          </div>
        </div>
      )}

      <TomorrowReady />

      {/* New estimate requests from website */}
      {unread > 0 && (
        <Link to="/app/leads" onClick={dismissUnread} data-testid="banner-new-leads"
          className="flex items-center gap-4 rounded-2xl border border-green-500/40 bg-green-500/10 p-5 animate-slide-up">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-500/20 text-green-700 font-black text-lg">{unread}</div>
          <div className="flex-1">
            <div className="font-heading font-bold text-slate-900">{unread === 1 ? t("home_new_lead_1") : t("home_new_leads_n", { count: unread })}</div>
            <div className="text-xs text-slate-500 mt-0.5">{t("leads_title")} →</div>
          </div>
          <ArrowRight className="h-5 w-5 text-green-700" />
        </Link>
      )}

      {/* Empty state teach */}
      {/* S1: brand-new accounts see ONLY the Get-set-up checklist — the extra
          "create your first job" buttons appear once a job exists. */}
      {data && data.projects_count === 0 && (
        <div className="rounded-2xl border border-brand/40 bg-brand/5 p-6 text-center" data-testid="home-empty-note">
          <p className="text-sm font-semibold text-slate-600">{t("project_empty_hint")}</p>
        </div>
      )}

      {/* Recent logs */}
      {data?.recent_logs?.length > 0 && (
        <section>
          <h2 className="font-heading font-bold text-slate-900 text-lg mb-3">{t("home_recent_logs")}</h2>
          <div className="space-y-3">
            {data.recent_logs.map((l) => (
              <div key={l.id} className="bf-card p-4" data-testid={`home-log-${l.id}`}>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <span>{fmtWhen(l.created_at)}</span>
                  <span>·</span>
                  <span>{l.author_name}</span>
                  {l.weather?.temp_f != null && <span className="ml-auto text-slate-500">{l.weather.temp_f}°{wxCondition(l.weather) ? ` ${wxCondition(l.weather)}` : ""}</span>}
                </div>
                <div className="mt-2 text-sm text-slate-900 whitespace-pre-wrap">{l.text}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Business snapshot — general stats live at the bottom */}
      {data && (
        <section data-testid="home-stats">
          <h2 className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">{t("home_stats_title")}</h2>
          <div className="grid grid-cols-3 gap-2">
            <Stat testid="stat-projects" icon={HardHat} value={data?.projects_count || 0} label={t("home_stats_projects")} />
            <Stat testid="stat-photos" icon={Camera} value={data?.photos_count || 0} label={t("home_stats_photos")} />
            <Stat testid="stat-logs" icon={ClipboardList} value={data?.logs_count || 0} label={t("home_stats_logs")} />
          </div>
        </section>
      )}

      {/* Draw invoice preview — from a done draw milestone (Needs-you) */}
      {drawRow && createPortal(
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50" onClick={() => !drawBusy && setDrawRow(null)} data-testid="draw-invoice-sheet">
          <div className="w-full max-w-md rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-black text-slate-900">{t("draw_sheet_title")}</h3>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between gap-3"><span className="text-slate-500">{t("draw_sheet_job")}</span><span className="truncate font-semibold text-slate-900">{drawRow.project_name}</span></div>
              <div className="flex items-center justify-between gap-3"><span className="text-slate-500">{t("draw_sheet_draw")}</span><span className="truncate font-semibold text-slate-900">{drawRow.draw_label}</span></div>
              {drawRow.estimate_number && <div className="flex items-center justify-between gap-3"><span className="text-slate-500">{t("draw_sheet_estimate")}</span><span className="font-semibold text-slate-900">{drawRow.estimate_number}</span></div>}
              <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-2"><span className="text-slate-500">{t("draw_sheet_amount")}</span><span className="text-lg font-black text-slate-900">{fmtMoney(drawRow.amount_cents)}</span></div>
            </div>
            <p className="mt-3 text-xs text-slate-500">{t("draw_sheet_note")}</p>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => setDrawRow(null)} disabled={drawBusy} data-testid="draw-cancel"
                className="min-h-[48px] flex-1 rounded-full border border-slate-300 text-sm font-bold text-slate-600 active:scale-95 disabled:opacity-50">{t("cancel")}</button>
              <button type="button" onClick={createDrawInvoice} disabled={drawBusy} data-testid="draw-create"
                className="min-h-[48px] flex-1 rounded-full bg-brand text-sm font-bold text-white active:scale-95 disabled:opacity-50">
                {drawBusy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("draw_create")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Send-to-customer share sheet — opens right after a draw invoice is created */}
      <ShareLinkSheet open={!!drawShare} onClose={() => setDrawShare(null)}
        title={t("draw_send_title")} message={t("draw_sms_body", { label: drawShare?.label || "" })}
        url={drawShare?.url || ""} phone={drawShare?.phone || ""} email={drawShare?.email || ""}
        onSendEmail={async (em) => { await api.post(`/fin/invoices/${drawShare.invId}/send`, { email: em }); }} />
    </div>
  );
}

function Stat({ testid, icon: Icon, value, label }) {
  return (
    <div className="bf-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-brand" />
      </div>
      <div className="mt-2 text-2xl font-black text-slate-900 font-heading" aria-label={`${value} ${label}`}>{value}</div>
    </div>
  );
}

function QuickAction({ to, onClick, icon: Icon, label, primary = false, big = false, testid }) {
  const cls = `relative flex ${big ? "items-center justify-center gap-3 min-h-[88px]" : "flex-col items-start gap-2 min-h-[64px]"} rounded-2xl border p-4 transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] ${
    primary ? "border-brand bg-brand text-white bf-brand-shadow" : "border-slate-200 bg-white text-slate-900 hover:border-brand"
  }`;
  const inner = (<>
    <Icon className={`${big ? "h-6 w-6" : "h-5 w-5"} ${primary ? "text-white" : "text-brand"}`} />
    <span className={`${big ? "font-heading text-lg font-extrabold" : "text-sm font-bold"} leading-tight ${primary ? "text-white" : ""}`}>{label}</span>
  </>);
  if (onClick) return <button type="button" onClick={onClick} data-testid={testid} className={`${cls} w-full text-left`}>{inner}</button>;
  return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
}
